import React, { useState, useEffect } from 'react';
import { Plus, Edit, Search, Download, UserPlus, Calendar, X, MessageSquare, Eye } from 'lucide-react';
import * as XLSX from 'xlsx';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

export default function JefaturaDocentesAsignaturas() {
    const [asignaturas, setAsignaturas] = useState([]);
    const [asignaciones, setAsignaciones] = useState([]);
    const [carreras, setCarreras] = useState([]);
    const [menciones, setMenciones] = useState([]);
    const [docentes, setDocentes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    // Filtros
    const [filtroGestion, setFiltroGestion] = useState(new Date().getFullYear());
    const [filtroCarrera, setFiltroCarrera] = useState('');
    const [filtroMencion, setFiltroMencion] = useState('');
    const [filtroNivel, setFiltroNivel] = useState('');

    // Modales
    const [showModal, setShowModal] = useState(false);
    const [showCalendarModal, setShowCalendarModal] = useState(false);
    const [filtroSala, setFiltroSala] = useState('');  // Filter for sala in calendar view
    const [filtroDocente, setFiltroDocente] = useState('');
    const [filtroDia, setFiltroDia] = useState('');
    const [selectedObservation, setSelectedObservation] = useState(null);

    const [selectedAsignatura, setSelectedAsignatura] = useState(null);
    const [formData, setFormData] = useState({
        id_docente: '',
        paralelo: '',
        capacidad: 30,
        dias: [],
        color: '#3B82F6',
        horarios_detalle: {}
    });

    const gestiones = [2023, 2024, 2025, 2026, 2027, 2028];
    const niveles = [
        { value: 1, label: 'Primer Año' },
        { value: 2, label: 'Segundo Año' },
        { value: 3, label: 'Tercer Año' },
        { value: 4, label: 'Cuarto Año' }
    ];
    const diasSemana = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes'];
    const horasDisponibles = [];
    for (let h = 6; h <= 18; h++) {
        horasDisponibles.push(`${h.toString().padStart(2, '0')}:00`);
        horasDisponibles.push(`${h.toString().padStart(2, '0')}:30`);
    }

    useEffect(() => {
        loadData();
    }, [filtroGestion, filtroCarrera, filtroMencion, filtroNivel]);


    const handleDownloadExcel = () => {
        try {
            const rows = [];
            let rowCounter = 1;

            if (!asignaturasFiltradas || asignaturasFiltradas.length === 0) {
                alert('No hay datos para exportar');
                return;
            }

            asignaturasFiltradas.forEach((a) => {
                const paralelos = getAsignaciones(a.id_asignatura, a.id_carrera, a.id_mencion);

                if (paralelos.length === 0) {
                    // Fila básica si no hay asignaciones
                    rows.push({
                        'No': rowCounter++,
                        'Código': a.codigo || '',
                        'Asignatura': a.nombre || '',
                        'Docente': '-',
                        'Sala': '-',
                        'Paralelo': '-',
                        'Horario': '-',
                        'Días': '-',
                    });
                } else {
                    // Una fila por cada paralelo
                    paralelos.forEach((asig) => {
                        const det = asig.horarios_detalle && Array.isArray(asig.horarios_detalle) ? asig.horarios_detalle : null;

                        // Formatear Sala, Horario y Días
                        let sala = '-';
                        let horario = '-';
                        let dias = '-';

                        if (det && det.length > 0) {
                            sala = det.map(d => d.sala || '-').join(', ');
                            horario = det.map(d => `${d.hora_inicio?.substring(0, 5)} - ${d.hora_fin?.substring(0, 5)}`).join(', ');
                            dias = getDiasAbrev(det.map(d => d.dia));
                        } else {
                            sala = asig.sala || '-';
                            horario = `${asig.hora_inicio?.substring(0, 5)} - ${asig.hora_fin?.substring(0, 5)}`;
                            dias = getDiasAbrev(asig.dias);
                        }

                        rows.push({
                            'No': rowCounter++,
                            'Código': a.codigo || '',
                            'Asignatura': a.nombre || '',
                            'Docente': getDocenteNombre(asig.id_docente),
                            'Sala': sala,
                            'Paralelo': asig.paralelo || '-',
                            'Horario': horario,
                            'Días': dias,
                        });
                    });
                }
            });

            const ws = XLSX.utils.json_to_sheet(rows);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Docentes_Asignaturas');
            XLSX.writeFile(wb, `Docentes_Asignaturas_${filtroGestion}.xlsx`);
        } catch (error) {
            console.error('Error al exportar Excel:', error);
            alert('Error al generar el archivo Excel.');
        }
    };

    const loadData = async () => {
        setLoading(true);
        try {
            // Cargar asignaturas, carreras, menciones, docentes
            const params = new URLSearchParams();
            params.append('gestion', filtroGestion);
            if (filtroCarrera) params.append('id_carrera', filtroCarrera);
            if (filtroMencion) params.append('id_mencion', filtroMencion);
            if (filtroNivel) params.append('nivel', filtroNivel);

            const [asignaturasRes, carrerasRes, mencionesRes, docentesRes, asignacionesRes] = await Promise.all([
                fetch(`${API_URL}/jefatura/asignaturas?${params.toString()}`),
                fetch(`${API_URL}/jefatura/carreras`),
                fetch(`${API_URL}/jefatura/menciones`),
                fetch(`${API_URL}/jefatura/docentes`),
                fetch(`${API_URL}/jefatura/asignaciones-horario?gestion=${filtroGestion}`)
            ]);

            const asignaturasData = await asignaturasRes.json();
            const carrerasData = await carrerasRes.json();
            const mencionesData = await mencionesRes.json();
            const docentesData = await docentesRes.json();
            const asignacionesData = await asignacionesRes.json();

            setAsignaturas(asignaturasData.asignaturas || []);
            setCarreras(carrerasData.carreras || []);
            setMenciones(mencionesData.menciones || []);
            setDocentes(docentesData.docentes || []);
            setAsignaciones(asignacionesData.asignaciones || []);
        } catch (error) {
            console.error('Error cargando datos:', error);
        } finally {
            setLoading(false);
        }
    };

    // Obtener la asignación para una asignatura (si existe) - primera match
    const getAsignacion = (idAsignatura) => {
        return asignaciones.find(a =>
            parseInt(a.id_asignatura) === parseInt(idAsignatura) &&
            parseInt(a.gestion) === parseInt(filtroGestion)
        );
    };

    // Obtener SOLO las asignaciones/paralelos APROBADOS para una asignatura
    const getAsignaciones = (idAsignatura, idCarrera, idMencion) => {
        return asignaciones.filter(a => {
            if (parseInt(a.id_asignatura) !== parseInt(idAsignatura)) return false;
            if (parseInt(a.gestion) !== parseInt(filtroGestion)) return false;

            // FILTRO ESTRICTO: Solo mostrar si está aprobado por Dirección
            if (a.estado_direccion !== 'aprobado') return false;

            return true;
        });
    };

    const handleAsignarDocente = (asignatura) => {
        setSelectedAsignatura(asignatura);
        setFormData({
            id_docente: '',
            paralelo: '',
            capacidad: 30,
            dias: [],
            color: '#3B82F6',
            horarios_detalle: {},
            id_asignacion: null
        });
        setShowModal(true);
    };

    const handleEditarParalelo = (asignatura, asignacion) => {
        setSelectedAsignatura(asignatura);

        // Cargar horarios_detalle si existe, sino migrar de campos legacy
        let horariosDetalle = {};
        if (asignacion.horarios_detalle && Array.isArray(asignacion.horarios_detalle) && asignacion.horarios_detalle.length > 0) {
            asignacion.horarios_detalle.forEach(d => {
                horariosDetalle[d.dia] = {
                    hora_inicio: d.hora_inicio || '',
                    hora_fin: d.hora_fin || '',
                    sala: d.sala || ''
                };
            });
        } else if (asignacion.dias && asignacion.dias.length > 0) {
            asignacion.dias.forEach(dia => {
                horariosDetalle[dia] = {
                    hora_inicio: asignacion.hora_inicio?.substring(0, 5) || '',
                    hora_fin: asignacion.hora_fin?.substring(0, 5) || '',
                    sala: asignacion.sala || ''
                };
            });
        }

        setFormData({
            id_docente: asignacion.id_docente || '',
            paralelo: asignacion.paralelo || '',
            capacidad: asignacion.capacidad || 30,
            dias: asignacion.dias || [],
            color: asignacion.color || '#3B82F6',
            horarios_detalle: horariosDetalle,
            id_asignacion: asignacion.id_asignacion
        });
        setShowModal(true);
    };

    const handleGuardarAsignacion = async () => {
        if (!formData.id_docente) {
            alert('Debe seleccionar un docente');
            return;
        }
        if (!formData.paralelo || formData.paralelo.length < 1 || formData.paralelo.length > 2) {
            alert('El paralelo debe tener 1 o 2 caracteres (ej: A, A1, B2)');
            return;
        }
        if (formData.dias.length === 0) {
            alert('Debe seleccionar al menos un día');
            return;
        }
        // Validar que cada día tenga hora y sala
        for (const dia of formData.dias) {
            const det = formData.horarios_detalle[dia];
            if (!det?.hora_inicio || !det?.hora_fin || !det?.sala) {
                alert(`Complete hora inicio, hora fin y sala para ${dia}`);
                return;
            }
        }

        try {
            const isEdit = !!formData.id_asignacion;
            const url = isEdit
                ? `${API_URL}/jefatura/asignaciones-horario/${formData.id_asignacion}`
                : `${API_URL}/jefatura/asignaciones-horario`;

            // Construir horarios_detalle array
            const horariosDetalle = formData.dias.map(dia => ({
                dia,
                hora_inicio: formData.horarios_detalle[dia]?.hora_inicio || '',
                hora_fin: formData.horarios_detalle[dia]?.hora_fin || '',
                sala: formData.horarios_detalle[dia]?.sala || ''
            }));

            const res = await fetch(url, {
                method: isEdit ? 'PUT' : 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    id_asignatura: parseInt(selectedAsignatura.id_asignatura),
                    gestion: parseInt(filtroGestion),
                    id_docente: parseInt(formData.id_docente),
                    sala: horariosDetalle[0]?.sala || '',
                    paralelo: formData.paralelo.toUpperCase(),
                    hora_inicio: horariosDetalle[0]?.hora_inicio || '',
                    hora_fin: horariosDetalle[0]?.hora_fin || '',
                    dias: formData.dias,
                    color: formData.color,
                    id_carrera: selectedAsignatura.id_carrera || null,
                    id_mencion: selectedAsignatura.id_mencion || null,
                    horarios_detalle: horariosDetalle
                })
            });

            if (res.ok) {
                setShowModal(false);
                loadData();
            } else {
                const error = await res.json();
                alert('Error: ' + error.message);
            }
        } catch (error) {
            console.error('Error guardando:', error);
            alert('Error al guardar la asignación');
        }
    };

    // Filtrar menciones por carrera
    const getMencionesFiltradas = () => {
        if (!filtroCarrera) return [];
        return menciones.filter(m => m.id_carrera === parseInt(filtroCarrera));
    };

    // Obtener nombre de carrera
    const getCarreraNombre = (id) => carreras.find(c => c.id_carrera === id)?.nombre || '-';

    // Obtener nombre de mención
    const getMencionNombre = (id) => menciones.find(m => m.id_mencion === id)?.nombre || '-';

    // Obtener nombre de docente
    // Obtener nombre de docente
    const getDocenteNombre = (id) => {
        if (!id || !docentes) return '-';
        const d = docentes.find(doc => doc && parseInt(doc.id_docente) === parseInt(id));
        return (d && d.nombres) ? `${d.nombres} ${d.apellidos}` : '-';
    };

    // Obtener nivel como texto
    const getNivelTexto = (nivel) => {
        const n = niveles.find(l => l.value === nivel);
        return n ? n.label : `Año ${nivel}`;
    };

    // Filtrar asignaturas por búsqueda (Nombre, Código, Docente, Sala, Paralelo, Año)

    const asignaturasFiltradas = (() => {
        // 1. Si no hay búsqueda, devolver TODAS las asignaturas (pero deduplicadas)
        if (!searchTerm || searchTerm.trim() === '') {
            const unique = new Map();
            asignaturas.forEach(item => {
                if (!unique.has(item.id_asignatura)) {
                    unique.set(item.id_asignatura, item);
                }
            });
            return Array.from(unique.values());
        }

        const search = searchTerm.toLowerCase().trim();

        // 2. Filtrar
        const filtered = asignaturas.filter(a => {
            // Matches asignatura info
            const codigo = (a.codigo || '').toLowerCase();
            const nombre = (a.nombre || '').toLowerCase();
            const nivel = getNivelTexto(a.nivel).toLowerCase();

            if (codigo.includes(search) ||
                nombre.includes(search) ||
                nivel.includes(search)) {
                return true;
            }

            // Matches assignments info
            const assignments = getAsignaciones(a.id_asignatura, a.id_carrera, a.id_mencion) || [];
            return assignments.some(asig => {
                const docenteName = getDocenteNombre(asig.id_docente).toLowerCase();
                const sala = (asig.sala || '').toLowerCase();
                const paralelo = (asig.paralelo || '').toLowerCase();

                return docenteName.includes(search) ||
                    sala.includes(search) ||
                    paralelo.includes(search);
            });
        });

        // 3. Deduplicar resultados filtrados
        const uniqueFiltered = new Map();
        filtered.forEach(item => {
            if (!uniqueFiltered.has(item.id_asignatura)) {
                uniqueFiltered.set(item.id_asignatura, item);
            }
        });
        return Array.from(uniqueFiltered.values());
    })();



    const toggleDia = (dia) => {
        setFormData(prev => {
            const newDias = prev.dias.includes(dia)
                ? prev.dias.filter(d => d !== dia)
                : [...prev.dias, dia];
            const newDetalle = { ...prev.horarios_detalle };
            if (!prev.dias.includes(dia)) {
                // Añadiendo día
                newDetalle[dia] = newDetalle[dia] || { hora_inicio: '', hora_fin: '', sala: '' };
            } else {
                // Quitando día
                delete newDetalle[dia];
            }
            return { ...prev, dias: newDias, horarios_detalle: newDetalle };
        });
    };

    const handleDetalleChange = (dia, field, value) => {
        setFormData(prev => ({
            ...prev,
            horarios_detalle: {
                ...prev.horarios_detalle,
                [dia]: {
                    ...prev.horarios_detalle[dia],
                    [field]: value
                }
            }
        }));
    };


    const [showNewAsigModal, setShowNewAsigModal] = useState(false);
    const [showApprovalModal, setShowApprovalModal] = useState(false);
    const [estadoGestion, setEstadoGestion] = useState(null);
    const [editingSubject, setEditingSubject] = useState(null);
    const [newAsigData, setNewAsigData] = useState({
        codigo: '',
        nombre: '',
        horas: 4,
        id_carrera: '',
        id_mencion: '',
        nivel: 1,
        prerequisito_codigo: ''
    });

    // Abrir modal para editar asignatura
    const openEditSubjectModal = (asig) => {
        setEditingSubject(asig);
        setNewAsigData({
            codigo: asig.codigo || '',
            nombre: asig.nombre || '',
            horas: asig.horas || 4,
            id_carrera: asig.id_carrera || '',
            id_mencion: asig.id_mencion || '',
            nivel: asig.nivel || 1,
            prerequisito_codigo: asig.prerequisito_codigo || ''
        });
        setShowNewAsigModal(true);
    };

    // Abrir modal para crear nueva asignatura
    const openNewAsigModal = () => {
        setEditingSubject(null);
        setNewAsigData({
            codigo: '',
            nombre: '',
            horas: 4,
            id_carrera: filtroCarrera || '',
            id_mencion: filtroMencion || '',
            nivel: filtroNivel || 1,
            prerequisito_codigo: ''
        });
        setShowNewAsigModal(true);
    };

    const handleCreateAsignatura = async () => {
        if (!newAsigData.codigo || !newAsigData.nombre || !newAsigData.id_carrera || !newAsigData.id_mencion) {
            alert('Por favor complete todos los campos obligatorios');
            return;
        }

        try {
            let res;
            if (editingSubject) {
                // Editar
                res = await fetch(`${API_URL}/jefatura/asignaturas/${editingSubject.id_asignatura}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(newAsigData)
                });
            } else {
                // Crear
                res = await fetch(`${API_URL}/jefatura/asignaturas`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(newAsigData)
                });
            }

            if (res.ok) {
                alert(editingSubject ? 'Asignatura actualizada' : 'Asignatura creada exitosamente');
                setShowNewAsigModal(false);
                setEditingSubject(null);
                setNewAsigData({
                    codigo: '',
                    nombre: '',
                    horas: 4,
                    id_carrera: '',
                    id_mencion: '',
                    nivel: 1,
                    prerequisito_codigo: ''
                });
                loadData();
            } else {
                const error = await res.json();
                alert('Error: ' + (error.message || 'Error al guardar'));
            }
        } catch (error) {
            console.error('Error guardando asignatura:', error);
            alert('Error al guardar asignatura');
        }
    };

    // Helper para mostrar días en calendario
    const getDiasAbrev = (diasArr) => {
        if (!diasArr || diasArr.length === 0) return '-';
        return diasArr.map(d => {
            if (d === 'Lunes') return 'L';
            if (d === 'Martes') return 'M';
            if (d === 'Miércoles') return 'MI';
            if (d === 'Jueves') return 'J';
            if (d === 'Viernes') return 'V';
            return d;
        }).join(', ');
    };

    if (loading) {
        return <div className="p-6 text-center">Cargando asignaturas...</div>;
    }

    return (
        <div className="p-6">
            <div className="mb-6">
                <h1 className="text-xl font-bold text-gray-800">
                    Jefatura de Carrera &gt; Docentes &gt; Asignaturas
                </h1>
            </div>

            {/* Toolbar y Filtros */}
            <div className="mb-6 bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                <div className="flex flex-wrap gap-4 items-end">
                    {/* Gestión */}
                    <div className="relative">
                        <label className="block text-xs font-semibold text-gray-500 mb-1 ml-1">GESTIÓN</label>
                        <select
                            value={filtroGestion}
                            onChange={e => setFiltroGestion(parseInt(e.target.value))}
                            className="px-4 py-2 border border-gray-300 rounded-full bg-white text-sm appearance-none pr-8 hover:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100 min-w-[100px]"
                        >
                            {gestiones.map(g => <option key={g} value={g}>{g}</option>)}
                        </select>
                        <span className="absolute right-3 top-[30px] pointer-events-none text-gray-400 text-xs">▼</span>
                    </div>
                    {/* Buscar y Descargar */}
                    <div className="flex-grow flex items-end gap-2">
                        <div className="relative flex-grow">
                            <label className="block text-xs font-semibold text-gray-500 mb-1 ml-1">BUSCAR</label>
                            <div className="relative">
                                <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
                                <input
                                    type="text"
                                    placeholder="Buscar: nombre, código, docente, sala, paralelo..."
                                    value={searchTerm}
                                    onChange={e => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 hover:border-blue-500"
                                />
                            </div>
                        </div>
                        <button
                            onClick={handleDownloadExcel}
                            className="p-2 bg-green-600 text-white rounded-full hover:bg-green-700 transition shadow-sm"
                            title="Descargar Excel"
                        >
                            <Download size={20} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Tabla */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-100">
                            <tr>
                                <th className="px-3 py-3 text-center font-semibold border">No</th>
                                <th className="px-3 py-3 text-center font-semibold border">Asignatura</th>
                                <th className="px-3 py-3 text-center font-semibold border">Docente</th>
                                <th className="px-3 py-3 text-center font-semibold border">Sala</th>
                                <th className="px-3 py-3 text-center font-semibold border">Paralelo</th>
                                <th className="px-3 py-3 text-center font-semibold border">Horario</th>
                                <th className="px-3 py-3 text-center font-semibold border">Días</th>
                            </tr>
                        </thead>
                        <tbody>
                            {asignaturasFiltradas.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="text-center py-12 text-gray-500">
                                        No hay asignaturas que coincidan con los filtros
                                    </td>
                                </tr>
                            ) : (
                                asignaturasFiltradas.map((a, idx) => {
                                    const paralelos = getAsignaciones(a.id_asignatura, a.id_carrera, a.id_mencion);
                                    const hasParalelos = paralelos.length > 0;

                                    // Si no tiene paralelos, mostrar una fila con datos vacíos
                                    if (!hasParalelos) {
                                        return (
                                            <tr key={a.id_asignatura} className="hover:bg-gray-50">
                                                <td className="px-3 py-2 text-center border">{idx + 1}</td>
                                                <td className="px-3 py-2 border">
                                                    <div className="font-semibold text-gray-800">{a.codigo}</div>
                                                    <div className="text-gray-600 text-xs">{a.nombre}</div>
                                                </td>
                                                <td className="px-3 py-2 text-center border" colSpan={5}>
                                                    <span className="text-gray-400 italic text-xs">Sin paralelos asignados</span>
                                                </td>
                                            </tr>
                                        );
                                    }

                                    // Si tiene paralelos, mostrar una fila por cada paralelo
                                    return paralelos.map((asig, pIdx) => (
                                        <tr key={`${a.id_asignatura}-${asig.id_asignacion || pIdx}`} className={`hover:bg-gray-50 ${pIdx > 0 ? 'bg-gray-50/50' : ''}`}>
                                            {/* Solo mostrar número y asignatura en la primera fila del grupo */}
                                            {pIdx === 0 ? (
                                                <>
                                                    <td className="px-3 py-2 text-center border" rowSpan={paralelos.length}>{idx + 1}</td>
                                                    <td className="px-3 py-2 border" rowSpan={paralelos.length}>
                                                        <div className="font-semibold text-gray-800">{a.codigo}</div>
                                                        <div className="text-gray-600 text-xs">{a.nombre}</div>
                                                        <div className="text-gray-400 text-[10px] mt-0.5">
                                                            {paralelos.length} paralelo{paralelos.length > 1 ? 's' : ''}
                                                        </div>
                                                    </td>
                                                </>
                                            ) : null}
                                            <td className="px-3 py-2 text-center border">
                                                <span  title={getDocenteNombre(asig.id_docente)}>
                                                    {getDocenteNombre(asig.id_docente)}
                                                </span>
                                            </td>
                                            <td className="px-3 py-2 text-center border">
                                                {(() => {
                                                    const det = asig.horarios_detalle && Array.isArray(asig.horarios_detalle) ? asig.horarios_detalle : null;
                                                    return det ? det.map((d, i) => <div key={i}>{d.sala || '-'}</div>) : (asig?.sala || '-');
                                                })()}
                                            </td>
                                            <td className="px-3 py-2 text-center border font-bold text-gray-700 font-mono">
                                                {asig?.paralelo || '-'}
                                            </td>
                                            <td className="px-3 py-2 text-center border text-xs whitespace-nowrap">
                                                {(() => {
                                                    const det = asig.horarios_detalle && Array.isArray(asig.horarios_detalle) ? asig.horarios_detalle : null;
                                                    if (det) {
                                                        return det.map((d, i) => (
                                                            <div key={i}>
                                                                <span >
                                                                    {d.hora_inicio?.substring(0, 5)} - {d.hora_fin?.substring(0, 5)}
                                                                </span>
                                                            </div>
                                                        ));
                                                    }
                                                    return (
                                                        <span className="bg-gray-100 px-2 py-1 rounded text-gray-700">
                                                            {asig.hora_inicio?.substring(0, 5)} - {asig.hora_fin?.substring(0, 5)}
                                                        </span>
                                                    );
                                                })()}
                                            </td>
                                            <td className="px-3 py-2 text-center border text-xs font-medium">
                                                {(() => {
                                                    const det = asig.horarios_detalle && Array.isArray(asig.horarios_detalle) ? asig.horarios_detalle : null;
                                                    if (det) {
                                                        return det.map((d, i) => (
                                                            <div key={i}>
                                                                <span >{d.dia?.substring(0, 2)}</span>
                                                            </div>
                                                        ));
                                                    }
                                                    return asig?.dias ? (
                                                        <span >{asig.dias.map(d => d.substring(0, 2)).join(', ')}</span>
                                                    ) : '-';
                                                })()}
                                            </td>
                                        </tr>
                                    ));
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
            {/* Modal Calendario (Nuevo) */}
            {showCalendarModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-7xl h-[90vh] flex flex-col overflow-hidden">
                        <div className="flex justify-between items-center px-6 py-4 border-b bg-gray-50">
                            <div>
                                <h2 className="text-xl font-bold text-blue-900">
                                    Horario General - Gestión {filtroGestion}
                                </h2>
                                
                            </div>
                            <div className="flex items-center gap-3">
                                {/* Filtro por Sala */}
                                <select
                                    value={filtroSala}
                                    onChange={(e) => setFiltroSala(e.target.value)}
                                    className="px-3 py-2 border border-gray-300 rounded-full text-sm bg-white focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="">Todas las Salas</option>
                                    {Array.from({ length: 20 }, (_, i) => i + 1).map(num => (
                                        <option key={num} value={num.toString()}>{`Sala ${num}`}</option>
                                    ))}
                                </select>

                                {/* Filtro por Docente */}
                                <select
                                    value={filtroDocente}
                                    onChange={(e) => setFiltroDocente(e.target.value)}
                                    className="px-3 py-2 border border-gray-300 rounded-full text-sm bg-white focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="">Todos los Docentes</option>
                                    {docentes.map(doc => (
                                        <option key={doc.id_docente} value={doc.id_docente}>
                                            {doc.nombres} {doc.apellidos}
                                        </option>
                                    ))}
                                </select>

                                {/* Filtro por Día */}
                                <select
                                    value={filtroDia}
                                    onChange={(e) => setFiltroDia(e.target.value)}
                                    className="px-3 py-2 border border-gray-300 rounded-full text-sm bg-white focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="">Todos los Días</option>
                                    {diasSemana.map(dia => (
                                        <option key={dia} value={dia}>{dia}</option>
                                    ))}
                                </select>

                                <button
                                    onClick={() => {
                                        // Aplicar mismos filtros para el Excel
                                        const exportFiltrados = asignaciones.filter(a => {
                                            // FILTRO: Solo aprobados
                                            if (a.estado_direccion !== 'aprobado') return false;

                                            if (filtroSala) {
                                                if (a.horarios_detalle && Array.isArray(a.horarios_detalle) && a.horarios_detalle.length > 0) {
                                                    if (!a.horarios_detalle.some(d => d.sala?.toString() === filtroSala.toString())) return false;
                                                } else {
                                                    if (a.sala?.toString() !== filtroSala.toString()) return false;
                                                }
                                            }
                                            if (filtroDocente && a.id_docente?.toString() !== filtroDocente.toString()) return false;
                                            if (filtroDia) {
                                                if (a.horarios_detalle && Array.isArray(a.horarios_detalle) && a.horarios_detalle.length > 0) {
                                                    if (!a.horarios_detalle.some(d => d.dia === filtroDia)) return false;
                                                } else {
                                                    if (!a.dias || !a.dias.includes(filtroDia)) return false;
                                                }
                                            }
                                            return true;
                                        });

                                        // Exportar horario a Excel
                                        try {
                                            const data = exportFiltrados.filter(a => (a.hora_inicio && a.hora_fin) || (a.horarios_detalle && a.horarios_detalle.length > 0)).map((a, idx) => {
                                                const asigInfo = asignaturas.find(as => parseInt(as.id_asignatura) === parseInt(a.id_asignatura));
                                                return {
                                                    'No': idx + 1,
                                                    'Código': asigInfo?.codigo || '-',
                                                    'Asignatura': asigInfo?.nombre || '-',
                                                    'Docente': getDocenteNombre(a.id_docente),
                                                    'Sala': a.sala || '-',
                                                    'Paralelo': a.paralelo || '-',
                                                    'Hora Inicio': a.hora_inicio || (a.horarios_detalle?.[0]?.hora_inicio || '-'),
                                                    'Hora Fin': a.hora_fin || (a.horarios_detalle?.[0]?.hora_fin || '-'),
                                                    'Días': a.dias?.join(', ') || a.horarios_detalle?.map(d => d.dia).join(', ') || '-'
                                                };
                                            });

                                            if (data.length === 0) {
                                                alert('No hay datos para exportar');
                                                return;
                                            }

                                            const ws = XLSX.utils.json_to_sheet(data);
                                            const wb = XLSX.utils.book_new();
                                            XLSX.utils.book_append_sheet(wb, ws, 'Horario');
                                            XLSX.writeFile(wb, `Horario_Matriculacion_${filtroGestion}.xlsx`);
                                        } catch (error) {
                                            console.error('Error al exportar Horario:', error);
                                            alert('Error al generar el archivo Excel.');
                                        }
                                    }}
                                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-full hover:bg-green-700 text-sm font-medium transition"
                                >
                                    <Download size={16} />
                                </button>
                                <button onClick={() => setShowCalendarModal(false)} className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-200 rounded-full transition">
                                    <X size={24} />
                                </button>
                            </div>
                        </div>

                        <div className="flex-grow overflow-auto p-4 bg-gray-100">
                            <div className="bg-white rounded-lg shadow min-w-[1000px]">
                                <table className="w-full border-collapse text-sm">
                                    <thead className="bg-gray-50 sticky top-0 z-10 shadow-sm">
                                        <tr>
                                            <th className="border px-2 py-3 min-w-[120px] text-gray-500 font-semibold bg-gray-50">Hora</th>
                                            {diasSemana.map(dia => (
                                                <th key={dia} className="border px-2 py-3 text-gray-700 font-bold bg-gray-50 min-w-[160px]">
                                                    {dia.toUpperCase()}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(() => {
                                            // Generar color único basado en string
                                            const stringToColor = (str) => {
                                                let hash = 0;
                                                for (let i = 0; i < str.length; i++) {
                                                    hash = str.charCodeAt(i) + ((hash << 5) - hash);
                                                }
                                                const hue = Math.abs(hash % 360);
                                                return `hsl(${hue}, 70%, 50%)`;
                                            };

                                            // Filtrar asignaciones según filtros (Sala, Docente, Día)
                                            const asignacionesFiltradas = asignaciones.filter(a => {
                                                // Filtrar por Sala
                                                if (filtroSala) {
                                                    if (a.horarios_detalle && Array.isArray(a.horarios_detalle) && a.horarios_detalle.length > 0) {
                                                        if (!a.horarios_detalle.some(d => d.sala?.toString() === filtroSala.toString())) return false;
                                                    } else {
                                                        if (a.sala?.toString() !== filtroSala.toString()) return false;
                                                    }
                                                }

                                                // Filtrar por Docente
                                                if (filtroDocente && a.id_docente?.toString() !== filtroDocente.toString()) return false;

                                                // Filtrar por Día
                                                if (filtroDia) {
                                                    if (a.horarios_detalle && Array.isArray(a.horarios_detalle) && a.horarios_detalle.length > 0) {
                                                        if (!a.horarios_detalle.some(d => d.dia === filtroDia)) return false;
                                                    } else {
                                                        if (!a.dias || !a.dias.includes(filtroDia)) return false;
                                                    }
                                                }

                                                return true;
                                            });

                                            // Pre-calcular qué clases cubren cada celda
                                            const cellClasses = {};

                                            asignacionesFiltradas.forEach(a => {
                                                if (!a.dias || a.dias.length === 0) return;

                                                // Si tiene horarios_detalle, usar horarios específicos por día
                                                if (a.horarios_detalle && Array.isArray(a.horarios_detalle) && a.horarios_detalle.length > 0) {
                                                    const claseId = `${a.id_asignatura}-${a.paralelo || ''}`;
                                                    a._color = a.color || stringToColor(claseId);

                                                    a.horarios_detalle.forEach(detalle => {
                                                        if (filtroDia && detalle.dia !== filtroDia) return;
                                                        if (filtroSala && detalle.sala?.toString() !== filtroSala.toString()) return;
                                                        if (!detalle.hora_inicio || !detalle.hora_fin) return;
                                                        const [inicioH, inicioM] = detalle.hora_inicio.substring(0, 5).split(':').map(Number);
                                                        const [finH, finM] = detalle.hora_fin.substring(0, 5).split(':').map(Number);
                                                        const inicioMinutos = inicioH * 60 + inicioM;
                                                        const finMinutos = finH * 60 + finM;

                                                        for (let mins = inicioMinutos; mins < finMinutos; mins += 30) {
                                                            const slotHour = Math.floor(mins / 60);
                                                            const slotMin = mins % 60;
                                                            const slotKey = `${detalle.dia}-${slotHour.toString().padStart(2, '0')}:${slotMin.toString().padStart(2, '0')}`;
                                                            if (!cellClasses[slotKey]) cellClasses[slotKey] = [];
                                                            cellClasses[slotKey].push({ ...a, _sala_dia: detalle.sala });
                                                        }
                                                    });
                                                } else {
                                                    // Fallback: usar campos legacy
                                                    if (!a.hora_inicio || !a.hora_fin) return;

                                                    const horaInicioStr = (a.hora_inicio || '').substring(0, 5);
                                                    const horaFinStr = (a.hora_fin || '').substring(0, 5);
                                                    const [inicioH, inicioM] = horaInicioStr.split(':').map(Number);
                                                    const [finH, finM] = horaFinStr.split(':').map(Number);
                                                    const inicioMinutos = inicioH * 60 + inicioM;
                                                    const finMinutos = finH * 60 + finM;

                                                    const claseId = `${a.id_asignatura}-${a.paralelo || ''}`;
                                                    a._color = a.color || stringToColor(claseId);

                                                    a.dias.forEach(diaH => {
                                                        if (filtroDia && diaH !== filtroDia) return;
                                                        if (filtroSala && a.sala?.toString() !== filtroSala.toString()) return;
                                                        for (let mins = inicioMinutos; mins < finMinutos; mins += 30) {
                                                            const slotHour = Math.floor(mins / 60);
                                                            const slotMin = mins % 60;
                                                            const slotKey = `${diaH}-${slotHour.toString().padStart(2, '0')}:${slotMin.toString().padStart(2, '0')}`;
                                                            if (!cellClasses[slotKey]) cellClasses[slotKey] = [];
                                                            cellClasses[slotKey].push(a);
                                                        }
                                                    });
                                                }
                                            });

                                            return horasDisponibles.map((hora) => (
                                                <tr key={hora} className="hover:bg-gray-50 transition-colors">
                                                    <td className="border px-2 py-2 text-center font-mono text-gray-500 bg-gray-50 border-r-2 border-r-gray-200">{hora}</td>
                                                    {diasSemana.map(dia => {
                                                        const cellKey = `${dia}-${hora}`;
                                                        const clasesEnCelda = cellClasses[cellKey] || [];

                                                        if (clasesEnCelda.length === 0) {
                                                            return (
                                                                <td key={cellKey} className="border p-0 h-12"></td>
                                                            );
                                                        }

                                                        return (
                                                            <td key={cellKey} className="border p-0">
                                                                <div className="flex gap-0 h-full w-full min-h-[40px]">
                                                                    {clasesEnCelda.map((clase, idx) => {
                                                                        const asignaturaInfo = asignaturas.find(as => parseInt(as.id_asignatura) === parseInt(clase.id_asignatura));
                                                                        const nombreAsig = asignaturaInfo ? asignaturaInfo.nombre : 'Desconocido';
                                                                        const codigoAsig = asignaturaInfo ? asignaturaInfo.codigo : '???';
                                                                        const docenteNombre = getDocenteNombre(clase.id_docente);

                                                                        return (
                                                                            <div
                                                                                key={`${clase.id_asignatura}-${clase.paralelo || idx}`}
                                                                                className="flex-1 px-1.5 h-full w-full text-white text-[11px] shadow-inner flex flex-col justify-center rounded-none"
                                                                                style={{ backgroundColor: clase._color }}
                                                                                title={`${codigoAsig} - ${nombreAsig}\nDocente: ${docenteNombre}\nSala: ${clase.sala || '-'}\nParalelo: ${clase.paralelo || '-'}\nHorario: ${clase.hora_inicio?.substring(0, 5)} - ${clase.hora_fin?.substring(0, 5)}`}
                                                                            >
                                                                                <div className="font-bold truncate">{codigoAsig} {clase.paralelo ? `(${clase.paralelo})` : ''}</div>
                                                                                <div className="truncate opacity-90 text-[10px]">{nombreAsig}</div>
                                                                                <div className="flex justify-between text-[9px] opacity-75 mt-0.5">
                                                                                    <span>S{clase.sala || '?'}</span>
                                                                                    <span className="truncate max-w-[60px]">{docenteNombre.split(' ')[0]}</span>
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            ));
                                        })()}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
